import { BrandPageQueryObj } from "@faire/retailer-shared/search/BrandPageQueryObj";
import {
  loggedInBrandLeanProductTilesRequest,
  getEstimatedClientSize,
  searchBrandProductsFiltersRequestFromProductTilesRequest,
  getPageNumber,
  getPageSize,
} from "@faire/retailer-shared/search/loggedInBrandLeanProductTilesRequest";
import getPublishedBrandProductCollectionMetadata from "@faire/web-api--source/api/brand/brandToken/published-product-collection-metadata/get";
import { getQueryKey as getQueryKeyPublishedBrandProductCollectionMetadata } from "@faire/web-api--source/api/brand/brandToken/published-product-collection-metadata/get-hooks";
import searchProductsFromBrandFilters from "@faire/web-api--source/api/v2/search/products/from-brand/filters/post";
import { getQueryKey_escape_hatch as getQueryKeyProductsFromBrandFilters } from "@faire/web-api--source/api/v2/search/products/from-brand/filters/post-hooks";
import searchProductsFromBrand from "@faire/web-api--source/api/v2/search/products/from-brand/post";
import { getQueryKey_escape_hatch as getQueryKeyProductsFromBrand } from "@faire/web-api--source/api/v2/search/products/from-brand/post-hooks";
import { QueryClient } from "@tanstack/react-query";

import { getSettingShouldDisplayRetrievalAndRankingDebugInfo } from "@faire/retailer/settings/getSettingShouldDisplayRetrievalAndRankingDebugInfo";
import { UserStore } from "@faire/retailer/stores/domain/UserStore";

import { getBrandViewUseQueryArgs } from "./hooks/useFetchCachedBrandView";

export function preloadBrandPageLoggedIn(
  brandToken: string,
  queryParams: BrandPageQueryObj,
  queryClient: QueryClient
) {
  queryClient.prefetchQuery(getBrandViewUseQueryArgs(brandToken));

  const {
    collectionsQueryKey,
    collectionsQueryFn,
    productsQueryKey,
    productsQueryFn,
    productFiltersQueryKey,
    productFiltersQueryFn,
    staleTime,
  } = getBrandProductsUseQueryArgs(brandToken, queryParams);

  queryClient.prefetchQuery({
    queryKey: collectionsQueryKey,
    queryFn: collectionsQueryFn,
    staleTime,
  });

  queryClient.prefetchQuery({
    queryKey: productsQueryKey,
    queryFn: productsQueryFn,
    staleTime,
  });

  queryClient.prefetchQuery({
    queryKey: productFiltersQueryKey,
    queryFn: productFiltersQueryFn,
    staleTime,
  });
}

export function getBrandProductsUseQueryArgs(
  brandToken: string,
  queryParams: BrandPageQueryObj
) {
  const clientSize = getEstimatedClientSize();

  const productsRequest = loggedInBrandLeanProductTilesRequest(
    queryParams,
    clientSize,
    brandToken,
    UserStore.get().user,
    getSettingShouldDisplayRetrievalAndRankingDebugInfo()
  );

  const filtersRequest =
    searchBrandProductsFiltersRequestFromProductTilesRequest(productsRequest);

  const collectionsQueryKey =
    getQueryKeyPublishedBrandProductCollectionMetadata(brandToken, {
      page: getPageNumber(queryParams),
      pageSize: getPageSize(clientSize),
    });

  const productsQueryKey = getQueryKeyProductsFromBrand(productsRequest);

  const productFiltersQueryKey =
    getQueryKeyProductsFromBrandFilters(filtersRequest);

  return {
    collectionsQueryKey,
    collectionsQueryFn: () =>
      getPublishedBrandProductCollectionMetadata(brandToken, {
        page: getPageNumber(queryParams),
        pageSize: getPageSize(clientSize),
      }),
    productsQueryKey,
    productsQueryFn: () => searchProductsFromBrand(productsRequest),
    productFiltersQueryKey,
    productFiltersQueryFn: () => searchProductsFromBrandFilters(filtersRequest),
    queryKeyString: JSON.stringify([collectionsQueryKey, productsQueryKey]),
    staleTime: 1000 * 60 * 5,
  };
}
